import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CSVDropzoneProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
}

export function CSVDropzone({ onFileSelect, isLoading }: CSVDropzoneProps) {
  const { t } = useTranslation(['clients']);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const validateAndSelectFile = useCallback((file: File) => {
    setError(null);
    
    // Check file type - support CSV and Excel
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const validTypes = [
      'text/csv', 
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];
    
    const isValidFile = validTypes.includes(file.type) || 
                        validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!isValidFile) {
      setError(t('import.errors.invalidFileType'));
      return;
    }
    
    // Check file size (max 10MB for Excel)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('import.errors.fileTooLarge'));
      return;
    }
    
    onFileSelect(file);
  }, [onFileSelect, t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSelectFile(file);
    }
  }, [validateAndSelectFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSelectFile(file);
    }
  }, [validateAndSelectFile]);

  return (
    <div className="space-y-2">
      <label
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/50',
          isLoading && 'pointer-events-none opacity-60'
        )}
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4 text-center">
          {isLoading ? (
            <>
              <Loader2 className="h-10 w-10 text-primary mb-3 animate-spin" />
              <p className="text-sm text-muted-foreground">{t('import.parsing')}</p>
            </>
          ) : (
            <>
              {isDragging ? (
                <FileSpreadsheet className="h-10 w-10 text-primary mb-3" />
              ) : (
                <Upload className="h-10 w-10 text-muted-foreground mb-3" />
              )}
              <p className="mb-2 text-sm font-medium">
                {t('import.dropzone.title')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('import.dropzone.subtitle')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('import.dropzone.formats')}
              </p>
            </>
          )}
        </div>
        <input
          type="file"
          className="hidden"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileInput}
          disabled={isLoading}
        />
      </label>
      
      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}
    </div>
  );
}
